/**
 * Request throttling to prevent excessive API calls
 * Acts as a final safety net to protect against bugs causing request loops
 */

interface ThrottleEntry {
  lastRequest: number;
  requestCount: number;
}

class RequestThrottle {
  private requests: Map<string, ThrottleEntry> = new Map();
  private readonly minInterval: number;
  private readonly maxRequestsPerMinute: number;

  constructor(minInterval: number = 1000, maxRequestsPerMinute: number = 10) {
    this.minInterval = minInterval;
    this.maxRequestsPerMinute = maxRequestsPerMinute;
  }

  shouldAllowRequest(key: string): boolean {
    const now = Date.now();
    const entry = this.requests.get(key);

    if (!entry) {
      this.requests.set(key, {
        lastRequest: now,
        requestCount: 1
      });
      return true;
    }

    const timeSinceLastRequest = now - entry.lastRequest;

    if (timeSinceLastRequest < this.minInterval) {
      console.warn(`[RequestThrottle] Blocked request to ${key} - too soon (${timeSinceLastRequest}ms < ${this.minInterval}ms)`);
      return false;
    }

    const oneMinuteAgo = now - 60000;
    if (entry.lastRequest < oneMinuteAgo) {
      entry.requestCount = 0;
    }

    if (entry.requestCount >= this.maxRequestsPerMinute) {
      console.warn(`[RequestThrottle] Blocked request to ${key} - too many requests (${entry.requestCount}/${this.maxRequestsPerMinute} per minute)`);
      return false;
    }

    entry.lastRequest = now;
    entry.requestCount++;
    this.requests.set(key, entry);

    return true;
  }

  reset(key?: string): void {
    if (key) {
      this.requests.delete(key);
    } else {
      this.requests.clear();
    }
  }

  getStats() {
    const stats: Record<string, { lastRequest: number; requestCount: number; timeSince: number }> = {};
    const now = Date.now();

    this.requests.forEach((entry, key) => {
      stats[key] = {
        lastRequest: entry.lastRequest,
        requestCount: entry.requestCount,
        timeSince: now - entry.lastRequest
      };
    });

    return stats;
  }
}

export const requestThrottle = new RequestThrottle(5000, 6);

if (typeof window !== 'undefined') {
  (window as any).__requestThrottle = requestThrottle;
}
